/**
 * The demo backend for Settings › Domains: one organisation's custom domain,
 * held in memory for the lifetime of one `MockApiClient`.
 *
 * It refuses what the real service refuses (`modules.tenancy.domains` and
 * `modules.tenancy.hostnames`), with the same status codes and in the same
 * words, because the screen's job on those paths is to show the API's message —
 * a mock that always says yes could not demonstrate that it does.
 *
 * DNS is the one thing it cannot have: there is no zone to publish into. So
 * `publishRecords` stands in for the customer going to their DNS console, and
 * the demo starts with nothing published, which is the state the instructions
 * are written for.
 */

import { ApiError } from '@/lib/api'

import type { DomainsBackend } from './api'
import type { DomainCheck, DomainState, TenantDomain } from './types'

const RESERVED_TLDS = new Set([
  'localhost',
  'local',
  'internal',
  'intranet',
  'private',
  'corp',
  'home',
  'lan',
  'test',
  'invalid',
  'example',
  'alt',
  'onion',
])

const LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/
const TLD = /^[a-z]{2,63}$/

export interface MockDomainsOptions {
  now: () => Date
  /** The TmT-owned hostname: the CNAME target the instructions name. */
  platformHost: string
}

function normalise(value: string): string {
  const [host = ''] = value.trim().replace(/\.+$/, '').split(':')
  return host.toLowerCase()
}

export class MockDomainsBackend implements DomainsBackend {
  private domain: TenantDomain | null = null
  /** What the customer has published, as far as this demo is concerned. */
  private published = { cname: false, txt: false }
  private sequence = 0

  constructor(private readonly options: MockDomainsOptions) {}

  /** Stands in for the customer adding the records in their DNS console. */
  publishRecords(records: { cname?: boolean; txt?: boolean } = { cname: true, txt: true }): void {
    this.published = { ...this.published, ...records }
  }

  state(): Promise<DomainState> {
    return Promise.resolve(this.snapshot())
  }

  request(hostname: string): Promise<DomainState> {
    return this.run(() => {
      const wanted = this.validate(hostname)
      if (this.domain?.status === 'active') {
        if (this.domain.hostname === wanted) return this.snapshot()
        throw new ApiError(
          409,
          'conflict',
          `${this.domain.hostname} is the live custom domain for this organisation; remove it before adding another`,
        )
      }
      this.sequence += 1
      const token = `demo-verification-${this.sequence}${'x'.repeat(12)}`
      this.domain = {
        id: this.domain?.id ?? 'dom_demo',
        hostname: wanted,
        status: 'pending_dns',
        verification_token: token,
        records: [
          { type: 'CNAME', name: wanted, value: this.options.platformHost },
          { type: 'TXT', name: `_insights-verification.${wanted}`, value: token },
        ],
        requested_at: this.options.now().toISOString(),
        verified_at: null,
        activated_at: null,
        removed_at: null,
      }
      // A fresh token invalidates whatever was published for the old one.
      this.published = { cname: this.published.cname, txt: false }
      return this.snapshot()
    })
  }

  verify(): Promise<DomainState> {
    return this.run(() => {
      const domain = this.requested()
      const check = this.check()
      if (!check.verified) return this.snapshot(check)
      const now = this.options.now().toISOString()
      if (domain.status === 'pending_dns') {
        domain.status = 'verified'
        domain.verified_at = now
      }
      if (domain.status === 'verified') {
        domain.status = 'active'
        domain.activated_at = now
      }
      return this.snapshot(check)
    })
  }

  remove(): Promise<DomainState> {
    return this.run(() => {
      const domain = this.requested()
      domain.status = 'removed'
      domain.removed_at = this.options.now().toISOString()
      this.published = { cname: false, txt: false }
      return this.snapshot()
    })
  }

  private check(): DomainCheck {
    return {
      verified: this.published.cname && this.published.txt,
      points_at_platform: this.published.cname,
      token_published: this.published.txt,
      observed_addresses: this.published.cname ? ['20.0.0.1'] : [],
    }
  }

  private requested(): TenantDomain {
    if (!this.domain || this.domain.status === 'removed') {
      throw new ApiError(404, 'not_found', 'this organisation has no custom domain')
    }
    return this.domain
  }

  private validate(value: string): string {
    const hostname = normalise(value)
    if (!hostname) throw new ApiError(422, 'validation_error', 'a hostname is required')
    if (hostname === normalise(this.options.platformHost)) {
      throw new ApiError(
        422,
        'validation_error',
        `${hostname} is the TmT-owned hostname, which already serves every organisation`,
      )
    }
    const labels = hostname.split('.')
    if (labels.length < 2) {
      throw new ApiError(422, 'validation_error', `${hostname} is not a fully qualified domain name`)
    }
    if (labels.some((label) => !LABEL.test(label))) {
      throw new ApiError(
        422,
        'validation_error',
        `${hostname} is not a valid hostname: each label must be letters, digits and interior hyphens`,
      )
    }
    const tld = labels[labels.length - 1] ?? ''
    if (!TLD.test(tld)) {
      throw new ApiError(422, 'validation_error', `${hostname} does not end in a public top-level domain`)
    }
    if (RESERVED_TLDS.has(tld)) {
      throw new ApiError(
        422,
        'validation_error',
        `.${tld} is a reserved name that cannot be delegated or certificated; use a domain you own`,
      )
    }
    return hostname
  }

  private snapshot(check: DomainCheck | null = null): DomainState {
    return {
      platform_host: this.options.platformHost,
      domain: this.domain ? { ...this.domain, records: [...this.domain.records] } : null,
      check,
    }
  }

  /** Refusals arrive as a rejected promise, the way the HTTP client's do. */
  private run<T>(body: () => T): Promise<T> {
    try {
      return Promise.resolve(body())
    } catch (error) {
      return Promise.reject(error)
    }
  }
}
